// All privacy rules live in usage.ts. Dynamic import so the desk, never recorded,
// never fetches the chunk. Nothing here throws; calls before start are no-ops.
import { POSTHOG_UI_HOST } from '@alphadesk/config.ts';
import type { Viewer } from '@alphadesk/web/contract.ts';
import type { PostHog } from 'posthog-js';
import {
  keepsAttribute,
  keepsText,
  normalizePath,
  personOf,
  scrubProperties,
  scrubUrl,
  traitsOf,
  type UsageEvent,
} from './usage.ts';

export interface UsageConfig {
  projectKey: string;
  apiHost: string;
}

// Module-level, not a ref: StrictMode's double mount would start a second recorder.
let client: PostHog | null = null;
let starting = false;

// The first page is requested before the chunk loads; hold the latest and flush on start.
let pending: { path: string; search: string } | null = null;

export async function startUsage(config: UsageConfig | null, viewer: Viewer): Promise<void> {
  if (client || starting || config === null) return;
  const person = personOf(viewer);
  if (person === null) return;

  starting = true;
  try {
    const { default: posthog } = await import('posthog-js');
    posthog.init(config.projectKey, {
      api_host: config.apiHost,
      // Otherwise recording links would point at the proxy.
      ui_host: POSTHOG_UI_HOST,
      // Pinned so an SDK upgrade cannot change what is captured.
      defaults: '2026-08-30',
      person_profiles: 'identified_only',

      // `capturePage` sends normalized paths instead.
      capture_pageview: false,
      // Kept on explicitly: disabling pageviews would otherwise drop it.
      capture_pageleave: true,

      // Privacy: autocapture records pressed-element text unmasked (e.g. `Exit at 1,234.50`).
      autocapture: false,
      capture_heatmaps: true,
      rageclick: true,
      disable_surveys: true,

      mask_all_text: true,
      mask_all_element_attributes: true,
      sanitize_properties: scrubProperties,

      session_recording: {
        maskAllInputs: true,
        // Privacy: mask every text node and let `keepsText` allow chrome back. The SDK has
        // no unmask selector, so this pairing is the only way to allow-list.
        maskTextSelector: '*',
        maskTextFn: keepsText,
        maskAttributeFn: keepsAttribute,
        // Privacy: nothing drawn on canvas needs recording. Keep off.
        captureCanvas: { recordCanvas: false, canvasFps: 0, canvasQuality: '0.1' },
        // Privacy: bodies and headers carry positions, budgets and the Zerodha session.
        recordHeaders: false,
        recordBody: false,
      },
    });
    posthog.identify(person, traitsOf(viewer));
    client = posthog;
    if (pending) capturePage(pending.path, pending.search);
  } catch {
    // Fail quietly: `client` stays null and every later call is a no-op.
  } finally {
    starting = false;
  }
}

export function capturePage(path: string, search: string): void {
  if (!client) {
    pending = { path, search };
    return;
  }
  pending = null;
  client.capture('$pageview', {
    $current_url: scrubUrl(`${path}${search}`),
    $pathname: normalizePath(path),
  });
}

// Properties must never carry money; see usage.ts.
export function note(event: UsageEvent, properties?: Record<string, unknown>): void {
  client?.capture(event, properties);
}

export function usageSession(): string {
  return client?.get_session_id() ?? '';
}

// Call before a sign-out navigates, so the next person is not identified as this one.
export function stopUsage(): void {
  client?.reset();
  client = null;
  pending = null;
}
